import { ComponentPropsWithRef, ReactNode, useEffect, useRef, useState } from 'react'

import { TooltipContainerProvider } from '@/lib/core/components/Tooltip'

import styles from './ModalBase.module.css'

export type ModalBaseProps = {
  children: ReactNode
  isActive: boolean
  dataTestId?: string // Consistent naming
  onEscape?: () => void
} & ComponentPropsWithRef<'dialog'>

export const ModalBase = ({
  children,
  isActive,
  dataTestId,
  onEscape,
  ...rest
}: ModalBaseProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [dialogElement, setDialogElement] = useState<HTMLDialogElement | null>(null)

  useEffect(() => {
    setDialogElement(dialogRef.current)
  }, [])

  useEffect(() => {
    if (isActive) {
      dialogRef.current?.showModal()
    } else {
      dialogRef.current?.close()
    }
  }, [isActive, dialogRef.current])

  // Built-in Escape key handling via the dialog "cancel" event
  useEffect(() => {
    const dialogEl = dialogRef.current
    if (!dialogEl) return

    const handleCancel = (e: Event) => {
      // If a consumer provided an escape handler, keep controlled behavior:
      // prevent the native close and delegate upwards. Otherwise, allow the
      // dialog to close natively (maintains previous behavior for callers that
      // don't wire an escape handler).
      if (onEscape) {
        e.preventDefault()
        onEscape()
      }
    }

    dialogEl.addEventListener('cancel', handleCancel as EventListener)
    return () => {
      dialogEl.removeEventListener('cancel', handleCancel as EventListener)
    }
  }, [isActive, onEscape])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current && onEscape) {
      onEscape()
    }
  }

  return (
    <dialog className={styles.modal} ref={dialogRef} data-testid={dataTestId} onClick={handleBackdropClick} {...rest}>
      <TooltipContainerProvider container={dialogElement}>{children}</TooltipContainerProvider>
    </dialog>
  )
}
